import { useState } from "react";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Search,
  Filter,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  FileText,
  User,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useAuth } from "../../contexts/AuthContext";

export interface Blog {
  _id: string;
  title: string;
  slug: string;
  description?: string;
  thumbnail?: string;
  author?: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
    role?: string;
  };
  type: string;
  isPublished: boolean;
  publishedAt?: string;
  createdAt: string;
  views: number;
  likes: number;
}

interface BlogTableProps {
  blogs: Blog[];
  description?: string;
  isLoading?: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: string;
  typeFilter: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (status: string) => void;
  onTypeChange: (type: string) => void;
  onPageChange: (page: number) => void;
  onAddClick: () => void;
  onEditClick: (blog: Blog) => void;
  onDeleteClick: (blog: Blog) => void;
  onTogglePublish: (blog: Blog) => void;
}

export function BlogTable({
  blogs,
  description = "Manage your blog posts and articles.",
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  searchTerm,
  statusFilter,
  typeFilter,
  onSearchChange,
  onStatusChange,
  onTypeChange,
  onPageChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onTogglePublish,
}: BlogTableProps) {
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('blogs:create');
  const canUpdate = hasPermission('blogs:update');
  const canDelete = hasPermission('blogs:delete');

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (isPublished: boolean) => {
    if (isPublished) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle2 size={12} className="mr-1" />
          Published
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <XCircle size={12} className="mr-1" />
        Draft
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      article: "bg-blue-100 text-blue-700",
      tutorial: "bg-purple-100 text-purple-700",
      file: "bg-emerald-100 text-emerald-700",
      slide: "bg-orange-100 text-orange-700",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded text-[11px] font-medium capitalize ${
          colors[type.toLowerCase()] || "bg-gray-100 text-gray-700"
        }`}
      >
        {type}
      </span>
    );
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Blogs
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div className="w-full xl:w-[400px]">
          <Input
            placeholder="Search blogs"
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-[44px] sm:h-[44px] justify-center"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter size={16} /> Filter
            </Button>
            {isFilterOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsFilterOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-2 min-w-[200px] animate-in fade-in zoom-in duration-150">
                  <div className="text-xs font-semibold text-gray-400 mb-1 px-2 uppercase">
                    Status
                  </div>
                  {["all", "published", "draft"].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        onStatusChange(status);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md capitalize ${
                        statusFilter === status
                          ? "bg-gray-100 font-bold text-[#e07828]"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                  <div className="h-px bg-gray-100 my-2" />
                  <div className="text-xs font-semibold text-gray-400 mb-1 px-2 uppercase">
                    Type
                  </div>
                  {["all", "article", "slide", "file", "tutorial"].map(
                    (type) => (
                      <button
                        key={type}
                        onClick={() => {
                          onTypeChange(type);
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md capitalize ${
                          typeFilter === type
                            ? "bg-gray-100 font-bold text-[#e07828]"
                            : "hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {type}
                      </button>
                    ),
                  )}
                </div>
              </>
            )}
          </div>

          {canCreate && (
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto h-[48px] sm:h-[44px]"
              onClick={onAddClick}
            >
              Add new blog
            </Button>
          )}
        </div>
      </div>

      {/* Single Scrollable Table View */}
      <div className="rounded-xl border border-gray-100 shadow-sm bg-white overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 font-semibold text-gray-900">
                  Blog Info
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900">
                  Author
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900">Type</th>
                <th className="px-6 py-4 font-semibold text-gray-900">Stats</th>
                <th className="px-6 py-4 font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900">
                  Created
                </th>
                <th className="px-6 py-4 w-[50px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center">
                      <div className="animate-spin h-6 w-6 border-2 border-[#e07828] border-t-transparent rounded-full mb-2" />
                      Loading blogs...
                    </div>
                  </td>
                </tr>
              ) : blogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No blogs found.
                  </td>
                </tr>
              ) : (
                blogs.map((blog) => (
                  <tr
                    key={blog._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0 border border-gray-100">
                          {blog.thumbnail ? (
                            <img
                              src={blog.thumbnail}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <FileText size={16} />
                            </div>
                          )}
                        </div>
                        <div className="max-w-[200px] xl:max-w-[300px]">
                          <p className="font-medium text-[#102040] line-clamp-1">
                            {blog.title}
                          </p>
                          <p className="text-xs text-gray-400 line-clamp-1">
                            /{blog.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {blog.author?.firstName || "Unknown"}{" "}
                          {blog.author?.lastName || ""}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">{getTypeBadge(blog.type)}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                        <span>{blog.views} views</span>
                        <span>{blog.likes} likes</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(blog.isPublished)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDate(blog.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === blog._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: blog._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>
                      <TableActionMenu
                        isOpen={activeMenu?.id === blog._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        {canUpdate && (
                          <button
                            onClick={() => {
                              onEditClick(blog);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2 size={14} className="text-blue-500" />
                            Edit Blog
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            onClick={() => {
                              onTogglePublish(blog);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            {blog.isPublished ? (
                              <>
                                <XCircle size={14} className="text-yellow-500" />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <CheckCircle2
                                  size={14}
                                  className="text-green-500"
                                />
                                Publish
                              </>
                            )}
                          </button>
                        )}
                        {canDelete && (
                          <div className="h-px bg-gray-100 my-1" />
                        )}
                        {canDelete && (
                          <button
                            onClick={() => {
                              onDeleteClick(blog);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete Blog
                          </button>
                        )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <p className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * pageSize + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> blogs
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="px-2"
            >
              &lt;
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1,
              )
              .map((p, i, arr) => (
                <div key={p} className="flex items-center">
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <span className="px-2 text-gray-400">...</span>
                  )}
                  <button
                    onClick={() => onPageChange(p)}
                    className={`w-8 h-8 rounded-md text-sm transition-colors ${
                      currentPage === p
                        ? "bg-[#e07828] text-white font-medium"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                </div>
              ))}
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="px-2"
            >
              &gt;
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
